import AdminRoute from '@/routes/AdminRoute';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import RouteErrorBoundary from '@/components/common/RouteErrorBoundary';
import RevealPageSkeleton from '@/components/common/RevealPageSkeleton';
import NotFound from '@/pages/NotFound';
import { OidcSecure } from '@axa-fr/react-oidc';
import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router';

const DataExplorer = lazy(() => import('@/pages/DataExplorer'));
const ReasonerChat = lazy(() => import('@/pages/ReasonerChat'));
const Projects = lazy(() => import('@/pages/Projects'));
const Workflows = lazy(() => import('@/pages/Workflows/Workflows'));
const PrepareData = lazy(() => import('@/pages/Workflows/PrepareData'));
const ModelDesigner = lazy(() => import('@/pages/Workflows/ModelDesigner'));
const FinalReview = lazy(() => import('@/pages/Workflows/FinalReview'));
const Mapping = lazy(() => import('@/pages/Mapping/MappingPage'));
const ReEnricher = lazy(() => import('@/pages/ReEnricher/ReEnricherPage'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const DashboardGroups = lazy(() => import('@/pages/DashboardGroups'));
const AdminPanel = lazy(() => import('@/pages/AdminPanel'));
const UserThreadsView = lazy(() => import('@/pages/AdminPanel/UserThreadsView'));
const HomepageLanding = lazy(() => import('@/pages/Homepage/HomepageLanding'));
const DashboardHomepageRoute = lazy(() => import('@/pages/Homepage/DashboardHomepageRoute'));

// The three reVeal paths render the same tree, so a chat started on `/` is
// not remounted — losing the reply it is streaming — when its first message
// moves it to the thread's URL. Landing only redirects on `/` itself.
const revealElement = (
  <HomepageLanding>
    <ReasonerChat />
  </HomepageLanding>
);

const routes = [
  {
    path: '/',
    element: revealElement,
    isProtected: true,
    suspenseFallback: <RevealPageSkeleton />,
  },
  {
    path: '/reVeal',
    element: revealElement,
    isProtected: true,
    suspenseFallback: <RevealPageSkeleton />,
  },
  {
    path: '/reVeal/thread/:threadId',
    element: revealElement,
    isProtected: true,
    suspenseFallback: <RevealPageSkeleton />,
  },
  {
    path: '/reMap',
    element: <DataExplorer />,
    isProtected: true,
  },
  {
    path: '/reFocus',
    element: <Dashboard />,
    isProtected: true,
  },
  {
    path: '/reFocus/groups',
    element: <DashboardGroups />,
    isProtected: true,
  },
  {
    path: '/reFocus/:projectId/:dashboardId',
    element: <DashboardHomepageRoute />,
    isProtected: true,
  },
  {
    path: '/reFocus/:id',
    element: <Dashboard />,
    isProtected: true,
  },
  {
    path: '/projects',
    element: <Projects />,
    isProtected: true,
  },
  {
    path: '/workflows',
    element: <Workflows />,
    isProtected: true,
  },
  {
    // The mapping panel replaces the old three-step reDesign flow. Its screens
    // are left in the codebase, and still reachable per workflow from the
    // workflows list, until their own removal is scheduled; nothing in the
    // product navigates here any more.
    path: '/reDesign/prepare-data',
    element: <Navigate to='/reDesign/mapping' replace />,
    isProtected: true,
  },
  {
    path: '/reDesign/mapping',
    element: <Mapping />,
    isProtected: true,
  },
  {
    path: '/reDesign/:id/prepare-data',
    element: <PrepareData />,
    isProtected: true,
  },
  {
    path: '/reDesign/:id/model-designer',
    element: <ModelDesigner />,
    isProtected: true,
  },
  {
    path: '/reDesign/:id/final-review',
    element: <FinalReview />,
    isProtected: true,
  },
  {
    path: '/reEnrich',
    element: <ReEnricher />,
    isProtected: true,
  },
  {
    path: '/admin',
    element: (
      <AdminRoute>
        <AdminPanel />
      </AdminRoute>
    ),
    isProtected: true,
  },
  {
    path: '/admin/user-management',
    element: (
      <AdminRoute>
        <AdminPanel />
      </AdminRoute>
    ),
    isProtected: true,
  },
  {
    path: '/admin/api-tokens',
    element: (
      <AdminRoute>
        <AdminPanel />
      </AdminRoute>
    ),
    isProtected: true,
  },
  {
    path: '/admin/fgac',
    element: (
      <AdminRoute>
        <AdminPanel />
      </AdminRoute>
    ),
    isProtected: true,
  },
  {
    path: '/admin/user-management/:userId/threads',
    element: (
      <AdminRoute>
        <UserThreadsView />
      </AdminRoute>
    ),
    isProtected: true,
  },
  {
    path: '*',
    element: <NotFound />,
    isProtected: false,
  },
];

const AppRoutes = () => {
  return (
    <Routes>
      {routes.map((route) => {
        const fallback =
          'suspenseFallback' in route && route.suspenseFallback
            ? route.suspenseFallback
            : <LoadingSpinner />;
        return (
          <Route
            key={route.path}
            path={route.path}
            element={
              route.isProtected ? (
                <OidcSecure>
                  <RouteErrorBoundary>
                    <Suspense fallback={fallback}>{route.element}</Suspense>
                  </RouteErrorBoundary>
                </OidcSecure>
              ) : (
                route.element
              )
            }
          />
        );
      })}
    </Routes>
  );
};

export default AppRoutes;
